import React, { useEffect, useRef, useState } from "react";
import { api, V1 } from "@/lib/api";
import { MessageSquare, Send, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LockedModule } from "./SchoolAnalytics";

export default function SchoolMessaging() {
  const [modules, setModules] = useState([]);
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const mod = modules.find((m) => m.key === "school_messaging");
  const owned = mod?.owned;

  const loadModules = () => api.get(`${V1}/school-modules`).then((r) => setModules(r.data || []));
  const loadThreads = () => api.get(`${V1}/school/messages/threads`).then((r) => setThreads(r.data || []));
  const loadThread = (parent_id) => api.get(`${V1}/school/messages/thread/${parent_id}`).then((r) => {
    setMessages(r.data.messages || []);
    setActive({ parent_id, parent: r.data.parent });
    setTimeout(() => scrollRef.current?.scrollTo({ top: 1e6, behavior: "smooth" }), 50);
  });

  useEffect(() => { loadModules(); }, []);
  useEffect(() => { if (owned) loadThreads(); }, [owned]);

  const send = async () => {
    if (!active || !text.trim()) return;
    setBusy(true);
    try {
      await api.post(`${V1}/school/messages/send`, { parent_id: active.parent_id, text: text.trim() });
      setText("");
      await loadThread(active.parent_id);
      loadThreads();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi"); }
    finally { setBusy(false); }
  };

  const filteredThreads = threads.filter((t) =>
    !q || (t.parent_name || "").toLowerCase().includes(q.toLowerCase()) || (t.parent_phone || "").includes(q)
  );

  if (!mod) return <div className="text-muted-foreground">Yükleniyor…</div>;
  if (!owned) return <LockedModule mod={mod} testid="messaging-locked" />;

  return (
    <div className="space-y-4" data-testid="school-messaging-page">
      <div>
        <div className="overline text-accent">Modül · {mod.label}</div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><MessageSquare className="h-7 w-7 text-primary" /> Veli Mesajlaşma</h1>
        <p className="text-muted-foreground text-sm mt-1">Başvuran velilerle direkt yazışabilirsiniz.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 h-[calc(100vh-260px)] min-h-[500px]">
        {/* THREADS */}
        <div className="rounded-xl border border-border bg-card flex flex-col overflow-hidden">
          <div className="p-3 border-b border-border flex items-center gap-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <Input className="h-8 border-none focus-visible:ring-0" placeholder="Veli ara..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="thread-search" />
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-border">
            {filteredThreads.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">Henüz mesaj yok.</div>}
            {filteredThreads.map((t) => (
              <button
                key={t.parent_id}
                onClick={() => loadThread(t.parent_id)}
                className={`w-full text-left p-3 hover:bg-muted/50 ${active?.parent_id === t.parent_id ? "bg-muted" : ""}`}
                data-testid={`thread-${t.parent_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm truncate">{t.parent_name}</div>
                  {t.unread_count > 0 && <span className="text-[10px] bg-accent text-accent-foreground rounded-full px-1.5 py-0.5">{t.unread_count}</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{t.last_sender_role === "school" ? "Siz: " : ""}{t.last_text}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{new Date(t.last_at).toLocaleString("tr-TR")}</div>
              </button>
            ))}
          </div>
        </div>

        {/* MESSAGES */}
        <div className="md:col-span-2 rounded-xl border border-border bg-card flex flex-col overflow-hidden">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Sol menüden bir veli seçin.</div>
          ) : (
            <>
              <div className="p-3 border-b border-border">
                <div className="font-display font-bold">{active.parent?.name || "Veli"}</div>
                <div className="text-xs text-muted-foreground">{active.parent?.phone}</div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.map((m) => (
                  <div key={m.id} className={`max-w-[70%] p-2.5 rounded-lg text-sm ${m.sender_role === "school" ? "ml-auto bg-primary text-primary-foreground" : "mr-auto bg-muted"}`}>
                    <div>{m.text}</div>
                    <div className="text-[10px] mt-1 opacity-70">{new Date(m.created_at).toLocaleString("tr-TR")}</div>
                  </div>
                ))}
                {messages.length === 0 && <div className="text-center text-sm text-muted-foreground">İlk mesajı gönderin.</div>}
              </div>
              <div className="p-3 border-t border-border flex gap-2">
                <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Mesajınızı yazın..." data-testid="msg-input"
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
                <Button onClick={send} disabled={busy || !text.trim()} data-testid="msg-send-btn"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
